import { useState } from "react";
import { Shield, Loader2, Mail, Lock, Eye, EyeOff, LogIn } from "lucide-react";
import { toast, Input, Label } from "@/shared/components";
import { supabase } from "@/shared/lib/supabase";

export function LoginForm({
  title = "앱 연결 로그인",
  description = "외부 앱이 datalab 데이터에 접근을 요청합니다. 계속하려면 로그인하세요.",
  icon,
}: {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
    } catch (err: any) {
      toast.error(err.message || "로그인에 실패했습니다");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-stroke-subtle bg-surface-raised p-8">
      <div className="mb-6 text-center">
        {icon ?? <Shield className="mx-auto mb-2 size-8 text-text-brand" />}
        <h1 className="text-xl font-bold">{title}</h1>
        <p className="mt-1 text-[13px] text-text-tertiary">{description}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label className="mb-1.5 block">이메일</Label>
          <Input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEmail(e.target.value)
            }
            leadingIcon={<Mail className="size-4" />}
          />
        </div>

        <div>
          <Label className="mb-1.5 block">비밀번호</Label>
          <Input
            type={showPw ? "text" : "password"}
            placeholder="비밀번호를 입력하세요"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPassword(e.target.value)
            }
            leadingIcon={<Lock className="size-4" />}
            trailingIcon={
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                aria-label={showPw ? "비밀번호 숨기기" : "비밀번호 표시"}
                className="text-text-tertiary hover:text-text-primary"
              >
                {showPw ? (
                  <EyeOff className="size-4" />
                ) : (
                  <Eye className="size-4" />
                )}
              </button>
            }
          />
        </div>

        <button
          type="submit"
          disabled={!email || !password || loading}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-accent py-2.5 text-[13px] font-semibold text-text-inverse transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {loading ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <LogIn className="size-4" />
          )}
          {loading ? "로그인 중..." : "로그인"}
        </button>
      </form>
    </div>
  );
}
